/**
=========================================================
* Material Dashboard 3 PRO React - Custom Sidenav
* Supports Mini + Full Logos with same theme design
=========================================================
*/

import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import { IconButton, Tooltip } from "@mui/material";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

// ✅ Logos
import fullLogo from "images/carepro.jpg";      // Full logo (expanded)
import miniLogo from "images/carepro.jpg";                // Mini logo (collapsed)
import FooterInfo from "track/FooterInfo";
import toggleImage from "images/carepro.jpg"; // 👈 use your image path

function Sidenav({ color, brandName, routes }) {
  const [openCollapse, setOpenCollapse] = useState(null);

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const hideTextOnMini = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: miniSidenav ? "none" : "block",
  };
  const textColor =
    transparentSidenav || (whiteSidenav && !darkMode) ? "dark" : "white";

  const handleMiniSidenav = () =>
    setMiniSidenav(dispatch, !miniSidenav);

  const renderRoutes = routes.map(
    ({ type, name, icon, key, route, collapse }) => {
      // ===== SUB MENU =====
      if (type === "collapse" && collapse) {
        return (
          <Tooltip
            key={key}
            title={miniSidenav ? name : ""}
            placement="right"
            arrow
            disableHoverListener={!miniSidenav}
          >
            <div>
              <SidenavCollapse
                icon={icon}
                name={name}                // ✅ ALWAYS PASS NAME
                open={openCollapse === key} // ✅ MANUAL CONTROL
                onClick={() =>
                  setOpenCollapse(openCollapse === key ? null : key)
                }
                sx={{
                  "& .MuiTypography-root": hideTextOnMini,
                }}
              >
                {collapse.map(({ name, route, key }) => (
                  <NavLink to={route} key={key}>
                    <SidenavItem
                      name={name}
                      nested
                      sx={{
                        "&.MuiListItem-root": {
                          minHeight: 36,
                        },

                        "& .MuiListItemButton-root:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.12)", // ✅ light white
                        },

                        "& .MuiListItemButton-root.Mui-selected": {
                          backgroundColor: "rgba(255, 255, 255, 0.18)", // ✅ selected
                        },

                        "& .MuiTypography-root": {
                          ...hideTextOnMini,
                          lineHeight: "1.2rem",
                        },
                      }}

                    />
                  </NavLink>
                ))}
              </SidenavCollapse>
            </div>
          </Tooltip>
        );
      }

      // ===== SINGLE ROUTE =====
      if (type === "route") {
        return (
          <NavLink to={route} key={key}>
            <Tooltip
              title={miniSidenav ? name : ""}
              placement="right"
              arrow
              disableHoverListener={!miniSidenav}
            >
              <div>
                <SidenavItem
                  color={color}
                  icon={
                    <Icon
                      sx={{
                        fontSize: "1.5rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {icon}
                    </Icon>
                  }
                  name={name} // ✅ always pass name
                  sx={{
                    "&.MuiListItem-root": {
                      minHeight: 36,
                    },

                    "& .MuiListItemButton-root:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)", // ✅ light white
                    },

                    "& .MuiListItemButton-root.Mui-selected": {
                      backgroundColor: "rgba(255, 255, 255, 0.18)", // ✅ selected
                    },

                    "& .MuiTypography-root": {
                      ...hideTextOnMini,
                      lineHeight: "1.2rem",
                    },
                  }}

                />
              </div>
            </Tooltip>
          </NavLink>
        );
      }

      if (type === "title") {
        return (
          <MDTypography
            key={key}
            color={textColor}
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={2}
            mt={2}
            mb={1}
            sx={hideTextOnMini}
          >
            {name}
          </MDTypography>
        );
      }

      if (type === "divider") {
        return <Divider key={key} />;
      }

      return null;
    }
  );

  return (
    <SidenavRoot
      variant="permanent"
      open={!miniSidenav}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "#1A237E !important", // ✅ FIRST COLOR CODE
          color: "#FFFFFF",
          border: "none",
        },
      }}
    >
      {/* HEADER */}
      <MDBox
        pt={2}
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <MDTypography
          variant="h6"
          color={textColor}
          sx={{
            display: miniSidenav ? "none" : "block",
          }}
        >
          {brandName}
        </MDTypography>

        <Tooltip title={miniSidenav ? "Expand" : "Collapse"}>
          <IconButton
            onClick={handleMiniSidenav}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: miniSidenav ? "center" : "space-between",
              padding: miniSidenav ? "4px" : "8px",
            }}
          >
            {/* Logo Container */}
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={{
                width: "100%",
                display: miniSidenav ? "none" : "flex",
                transition: "all 0.3s ease",
                marginRight: "35px", // Shift left more
              }}
            >
              <MDBox
                component="img"
                src="/careconnect.jpg"
                alt="Toggle"
                sx={{
                  width: "50px", // Reduced size
                  height: "auto",
                  borderRadius: "50%", // Optional: makes it look like an icon
                  marginBottom: "5px",
                  transition: "all 0.3s ease",
                }}
              />
              <MDTypography
                variant="button"
                fontWeight="bold"
                color="white"
                fontSize="0.75rem"
                sx={{ letterSpacing: "1px", textAlign: "center", lineHeight: 1.2 }}
              >
                CARE CONNECT
              </MDTypography>
            </MDBox>

            {/* Arrow */}
            <Icon
              sx={{
                fontSize: miniSidenav ? "1.6rem" : "2rem",
                transform: miniSidenav ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.3s ease",
                position: miniSidenav ? "relative" : "absolute",
                right: miniSidenav ? "auto" : "10px",
                top: miniSidenav ? "auto" : "10px",
              }}
            >
              {miniSidenav ? "chevron_right" : "chevron_left"}
            </Icon>
          </IconButton>
        </Tooltip>




      </MDBox>

      <Divider />

      <MDBox display="flex" flexDirection="column" height="100%">
        <List
          sx={{
            flexGrow: 1,
            py: 0,

            ...(miniSidenav && {
              "& .MuiListItem-root": {
                padding: 0,
                margin: "28px 0",
              },

              "& .MuiListItemButton-root": {
                minHeight: 38,            // 🔥 tighter
                padding: "6px",           // 🔥 tighter
                justifyContent: "center",
              },

              /* Material Dashboard icon wrapper */
              "& .MuiListItemButton-root > div:first-of-type": {
                minWidth: "auto",
                marginRight: 0,
                display: "flex",
                justifyContent: "center",
              },

              "& .MuiTypography-root": {
                display: "none",
              },
            }),

            "& .MuiListItemButton-root.Mui-selected": {
              borderRadius: "8px",
            },
          }}
        >

          {renderRoutes}
        </List>


        <FooterInfo />
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.array.isRequired,
};

export default Sidenav;
